import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { toast } from 'sonner'
import { 
  ArrowLeft, Users, Mail, Calendar, MessageSquare, 
  Copy, Eye, Clock, ChevronRight
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function TestersPage() {
  const navigate = useNavigate()
  const { token } = useAuth()
  const [testers, setTesters] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedTester, setSelectedTester] = useState(null)
  const [testerProfile, setTesterProfile] = useState(null)
  const [loadingProfile, setLoadingProfile] = useState(false)

  useEffect(() => {
    fetchTesters()
  }, [token])

  const fetchTesters = async () => {
    try {
      const response = await axios.get(`${API}/testers`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setTesters(response.data.testers)
      setTotal(response.data.total)
    } catch (error) {
      console.error('Error fetching testers:', error)
      if (error.response?.status === 403) {
        toast.error('Acceso denegado')
        navigate('/')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchTesterProfile = async (testerId) => {
    setLoadingProfile(true)
    try {
      const response = await axios.get(`${API}/testers/${testerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setTesterProfile(response.data)
      setSelectedTester(testerId)
    } catch (error) {
      console.error('Error fetching tester profile:', error)
      toast.error('Error al cargar perfil')
    } finally {
      setLoadingProfile(false)
    }
  }

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email)
    toast.success('Email copiado')
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('es-MX', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('es-MX', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    )
  }

  // Tester Profile View
  if (selectedTester && testerProfile) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-[88px] z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
          <div className="flex items-center gap-3 p-4 max-w-4xl mx-auto">
            <Button variant="ghost" size="icon" onClick={() => setSelectedTester(null)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">Perfil del Tester</h1>
              <p className="text-xs text-muted-foreground">{testerProfile.email}</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-4 pb-24">
          {/* Profile Card */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold">{testerProfile.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{testerProfile.email}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2"
                      onClick={() => copyEmail(testerProfile.email)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <Badge variant="secondary">
                  {testerProfile.locale === 'es-MX' ? 'Español' : 'English'}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                <div>
                  <p className="text-xs text-muted-foreground">Registrado</p>
                  <p className="text-sm font-medium">{formatDate(testerProfile.registered_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Último acceso</p>
                  <p className="text-sm font-medium">{formatDate(testerProfile.last_access)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Feedbacks enviados</p>
                  <p className="text-sm font-medium">{testerProfile.feedback_count}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">ID</p>
                  <p className="text-sm font-medium text-muted-foreground truncate">{testerProfile.id.slice(0, 8)}...</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedbacks */}
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Feedbacks de este tester ({testerProfile.feedbacks?.length || 0})
          </h3>
          
          <div className="space-y-3">
            {testerProfile.feedbacks?.length === 0 && (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  Este tester aún no ha enviado feedback
                </CardContent>
              </Card>
            )}
            
            {testerProfile.feedbacks?.map((fb, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{fb.category}</Badge>
                      <span className="text-xs text-muted-foreground">
                        Rating: {fb.rating}/5
                      </span>
                    </div>
                    <Badge className={
                      fb.status === 'new' ? 'bg-blue-500' :
                      fb.status === 'reviewed' ? 'bg-amber-500' : 'bg-emerald-500'
                    }>
                      {fb.status === 'new' ? 'Nuevo' :
                       fb.status === 'reviewed' ? 'Revisado' : 'Resuelto'}
                    </Badge>
                  </div>
                  <p className="text-sm mb-2">{fb.message}</p>
                  {fb.subscription_interest && (
                    <div className="mt-2 p-2 bg-emerald-500/10 rounded text-xs">
                      <strong>Interés en suscripción:</strong> {fb.subscription_interest}
                      {fb.subscription_comment && (
                        <p className="mt-1 text-muted-foreground">"{fb.subscription_comment}"</p>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {formatDateTime(fb.created_at)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Testers List View
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-[88px] z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center gap-3 p-4 max-w-4xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Testers Registrados</h1>
            <p className="text-xs text-muted-foreground">
              {total} testers únicos (por email)
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 pb-24">
        {/* Summary Card */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-sm text-muted-foreground">Testers únicos por email</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Testers List */}
        <div className="space-y-2">
          {testers.map((tester) => (
            <Card 
              key={tester.id} 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => fetchTesterProfile(tester.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium truncate">{tester.name}</p>
                      {tester.feedback_count > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {tester.feedback_count} feedback{tester.feedback_count > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        <span className="truncate max-w-[200px]">{tester.email}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(tester.registered_at)}</span>
                      </div>
                      {tester.last_access && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>Último: {formatDate(tester.last_access)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        copyEmail(tester.email)
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {testers.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-muted-foreground">No hay testers registrados aún</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
